/*
 *  -----------------------------------------------------------------------
 *    <copyright file="legacy-legacy-reports.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import { useBoolean, useTranslation, useAuthorization } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GENERATE_CSV_REPORT, GENERATE_PDF_REPORT } from 'queries/reports';
import Constants from 'utils/constants';
import { downloadBase64File } from 'utils/file-service';
import NotificationService from 'utils/notification';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import ReportTile from 'views/components/reports/report-tile';
import { AllRoutes } from '@utils/router/all-routes';

interface CentralMonitoringLegacyReportsProps {
    study: StudyFields;
}

interface ITemplate {
    title?: string;
    name: string;
    showView?: boolean;
    showDownload?: boolean;
    description: string;
    internal?: boolean;
    ismp?: boolean;
    route?: string;
}

const PDFTemplates: ITemplate[] = [
    {
        title: 'CentralMonitoringReport',
        name: 'CentralMonitoring',
        showView: true,
        description: 'centralMonitoringReportDescription',
        route: 'cm-review-report',
    },
];

const CSVTemplates: ITemplate[] = [
    {
        name: 'CentralMonitoringObservationsAndActionsCsv',
        showDownload: true,
        description: 'centralMonitoringObservationsAndActionsDescription',
    },
];

const CentralMonitoringLegacyReports = ({ study }: CentralMonitoringLegacyReportsProps) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const user = useAuthorization();
    const navigate = useNavigate();
    const generating = useBoolean(false);
    const [value, setValue] = useState(0);
    const canView = user?.canRead({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
    });

    const params = new URLSearchParams(window.location.search);

    useEffect(() => {
        if (params.get('pdfReports')) setValue(1);
    }, []);

    if (!study) return <Loading />;

    const generateReport = (isCsv: boolean, name: string) => {
        generating.setTrue();
        if (isCsv) {
            client
                .query({
                    query: GENERATE_CSV_REPORT,
                    fetchPolicy: 'network-only',
                    variables: { studyId: study.id, template: name },
                })
                .then((reply: any) => {
                    if (!reply?.data?.generateCsvReport) {
                        NotificationService.showNotification(t('errorMessage'));
                        return;
                    }

                    generating.setFalse();
                    downloadBase64File(`${name}.csv`, reply.data.generateCsvReport);
                })
                .catch((error) => {
                    NotificationService.showNotification(t('errorMessage'));
                    generating.setFalse();
                    console.error('Error generating report: ', error);
                });
        } else {
            client
                .mutate({
                    mutation: GENERATE_PDF_REPORT,
                    variables: { studyId: study.id, template: name },
                })
                .then((reply: any) => {
                    if (!reply?.data?.generatePdfReport) {
                        NotificationService.showNotification(t('errorMessage'));
                        return;
                    }

                    generating.setFalse();
                    downloadBase64File(`${name}.pdf`, reply.data.generatePdfReport.contents);
                })
                .catch((error) => {
                    NotificationService.showNotification(t('errorMessage'));
                    generating.setFalse();
                    console.error('Error generating report: ', error);
                });
        }
    };

    if (!canView) return <h1>{t('unauthorised')}</h1>;

    return (
        <>
            <PageTitle text={t('LegacyReports')} className="mb-10" />
            <Tabs
                value={value}
                onChange={(e, newValue) => setValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
            >
                <Tab label="CSV" className="format-tab" data-testid="csv-tab" />
                <Tab label="PDF" className="format-tab" data-testid="pdf-tab" />
            </Tabs>
            {value === 0 ? (
                <div className="reports-wrapper">
                    {CSVTemplates.map((item: ITemplate) => (
                        <div className="report-item" key={item.name}>
                            <ReportTile
                                title={t(`cm:${item.name}`)}
                                description={t(`cm:${item.description}`)}
                                showDownload
                                onClick={() => {
                                    generateReport(true, item.name);
                                }}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="reports-wrapper">
                    {PDFTemplates.map((item: ITemplate) => (
                        <div className="report-item" key={item.name}>
                            <ReportTile
                                title={t(`cm:${item.title}`)}
                                description={t(`cm:${item.description}`)}
                                showDownload={item.showDownload}
                                showView={item.showView}
                                onClick={() => {
                                    if (item.showDownload) generateReport(false, item.name);
                                    else if (item.route) navigate(AllRoutes.path(item.route));
                                    else
                                        navigate(
                                            AllRoutes.path('cm-pdf-report-details-id', {
                                                template: encodeURIComponent(item.name),
                                            }),
                                        );
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default CentralMonitoringLegacyReports;
